import { ContentBody } from "@app/components/core/ContentBody/ContentBody"
import { ContentFooter } from "@app/components/core/ContentFooter/ContentFooter"
import { ContentHeader } from "@app/components/core/ContentHeader/ContentHeader"
import Input from "@app/components/move2Library/Input/Input"
import OrderSubPage from "@app/components/pageTypes/OrderSubPage/OrderSubPage"
import { useGetActivityGroups, useGetStrings } from "@app/store/service/cmsService"
import { useCheckActivityAvailablity, useCheckRequiredVisitors, useCreateActivityReservation } from "@app/store/service/connectorService"
import { Image, classNamez, useViewport } from "@tecreation/funnel-components"
import { useCallback, useEffect, useReducer, useRef, useState } from "react"

import { Loader } from "@app/components/core/Loader/Loader"
import Button from "@app/components/move2Library/Button/Button"
import { useCustomNavigation } from "@app/components/navigation/NavigationHandler"
import { useRaiseError } from "@app/store/actions/errorActions"
import { addMassage } from "@app/store/actions/orderActions"
import { appStore } from "@app/store/store"
import { SCANCODE_TYPES } from "@app/utils/constants"
import { getPriceString, getTimeString } from "@app/utils/localeUtils"
import { faChevronDown } from "@fortawesome/free-solid-svg-icons/faChevronDown"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ResponseError, useSelector } from "@tecreation/js-state-react"
import { AnimatePresence, m } from "framer-motion"
import { useForm } from "react-hook-form"
import { useSearchParams } from "react-router"
import "./MassageBooking.css"

function getMinPrice(group, type) {
	let minPrice = 9999
	if (!type) {
		group.single.forEach(e => {
			if (e.price < minPrice) {
				minPrice = e.price
			}
		})

		group.pair.forEach(e => {
			if (e.price < minPrice) {
				minPrice = e.price
			}
		})
	} else {
		group[type].forEach(e => {
			if (e.price < minPrice) {
				minPrice = e.price
			}
		})
	}

	return minPrice
}

function Group({ selectedGroup, group, setGroup }) {
	const [expanded, setExpanded] = useState(false)
	const {
		data: { from },
	} = useGetStrings(undefined, { defaultData: {} })

	return (
		<div
			className={
				"rounded-3 boxShadow clickable massageGroup relative" +
				classNamez({
					selected: selectedGroup && selectedGroup.id === group.id,
				})
			}
			onClick={() => {
				setGroup(group)
			}}
			style={{
				height: "fit-content",
			}}
		>
			<Image src={group.image} asBackground className="rounded-top-3 massageGroup__image" />
			<div className="p-3 text-center">
				<div className="text-center regular">{group.title}</div>
				<div className="text-center color-fg-secondary mt-1">
					{from} {getPriceString(getMinPrice(group))}
				</div>
			</div>

			<div
				className="flex-center px-3 clickable p-2 pb-0"
				style={{
					position: "absolute",
					left: "50%",
					transform: "translateX(-50%)",
					bottom: "4px",
				}}
				onClick={e => {
					e.stopPropagation()
					setExpanded(!expanded)
				}}
			>
				<FontAwesomeIcon icon={faChevronDown} className={classNamez({ "fa-rotate-180 ": expanded })} />
			</div>
			<AnimatePresence>
				{expanded && (
					<m.div
						className="z-9 of-hidden w-100 px-4 mb-4"
						style={{ originY: 0 }}
						initial={{
							height: 0,
						}}
						animate={{
							height: "auto",
							transition: {
								duration: 0.4,
							},
						}}
						exit={{
							height: 0,
							transition: { duration: 0.4 },
						}}
					>
						{group.description}
					</m.div>
				)}
			</AnimatePresence>
		</div>
	)
}

function GroupSelection({ selectedGroup, setGroup }) {
	const { data: groups, isLoading } = useGetActivityGroups()
	const {
		data: { massageFunnelSelectMassage },
	} = useGetStrings(undefined, { defaultData: {} })

	return (
		<div className="mt-6">
			<div className="f3 regular text-center">{massageFunnelSelectMassage}</div>
			<Loader show={isLoading} />
			{!isLoading && (
				<div className="mt-3 gridSelection">
					{groups && (
						<>
							{groups.map((group, index) => {
								return <Group group={group} selectedGroup={selectedGroup} key={index} setGroup={setGroup} />
							})}
						</>
					)}
				</div>
			)}
		</div>
	)
}

function TypeSelection({ group, type, setType }) {
	const {
		data: { from, massageFunnelTypeSingle, massageFunnelTypePair, massageFunnelSelectPersons },
	} = useGetStrings(undefined, { defaultData: {} })

	const ref = useRef(undefined)
	useEffect(() => {
		if (group) {
			if (ref.current) {
				setTimeout(() => {
					ref.current.scrollIntoView({ behavior: "smooth" })
				}, 0)
			}
		}
	}, [group])

	if (!group) {
		return null
	}

	return (
		<div className="w-100 flex flex-center flex-column mt-6">
			<div className="f3 regular" ref={ref}>
				{massageFunnelSelectPersons}
			</div>
			<div className="flex flex-justify-between w-100 mt-3" style={{ maxWidth: "300px" }}>
				{group.single && group.single.length > 0 && (
					<div
						className={
							"primary border px-5 py-2 flex flex-center flex-column rounded-3 clickable" +
							classNamez({
								"color-border-primary": type !== "single",
								"color-bg-secondary": type === "single",
								"color-fg-on-secondary": type === "single",
							})
						}
						onClick={() => {
							setType("single")
						}}
					>
						<div className="text-center">
							<div>{massageFunnelTypeSingle}</div>
							<div>
								{from} {getPriceString(getMinPrice(group, "single"))}
							</div>
						</div>
					</div>
				)}
				{group.pair && group.pair.length > 0 && (
					<div
						className={
							"primary border px-5 py-2 flex flex-center flex-column rounded-3 clickable" +
							classNamez({
								"color-border-primary": type !== "pair",
								"color-bg-secondary": type === "pair",
								"color-fg-on-secondary": type === "pair",
							})
						}
						onClick={() => {
							setType("pair")
						}}
					>
						<div className="text-center">
							<div>{massageFunnelTypePair}</div>
							<div>
								{from} {getPriceString(getMinPrice(group, "pair"))}
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	)
}

function ActivitySelection({ type, group, activity, setActivity }) {
	const {
		data: { massageFunnelSelectDuration },
	} = useGetStrings(undefined, { defaultData: {} })

	const ref = useRef(undefined)

	useEffect(() => {
		if (type) {
			if (ref.current) {
				ref.current.scrollIntoView({ behavior: "smooth" })
			}
		}
	}, [type])

	if (!type) {
		return null
	}

	return (
		<div className="mt-6">
			<div className="f3 regular text-center" ref={ref}>
				{massageFunnelSelectDuration}
			</div>
			<div className="mt-3 gridSelection">
				{group[type].map((acc, i) => {
					return (
						<div
							className={
								"primary border px-3 py-2 flex flex-center flex-column rounded-3 clickable" +
								classNamez({
									"color-border-primary": activity && acc.id !== activity.id,
									"color-bg-secondary": activity && acc.id === activity.id,
									"color-fg-on-secondary": activity && acc.id === activity.id,
								})
							}
							onClick={() => {
								setActivity(acc)
							}}
							key={i}
						>
							<div>{acc.duration} min</div>
							<div
								className={
									"f5 color-fg-secondary" +
									classNamez({
										"color-fg-on-secondary": activity && acc.id === activity.id,
									})
								}
							>
								{getPriceString(acc.price, undefined, false)}
							</div>
						</div>
					)
				})}
			</div>
		</div>
	)
}

function TimeSelection({ activity, date, setTimeslot, timeslot, time }) {
	const [checkActivityAvailablity, { data, isLoading: loadCheck, error }] = useCheckActivityAvailablity()
	const {
		data: { massageFunnelSelectTime, massageFunnelError, massageFunnelNoTimeSlots },
	} = useGetStrings(undefined, { defaultData: {} })

	const ref = useRef(undefined)

	useEffect(() => {
		if (activity && date) {
			if (ref.current) {
				ref.current.scrollIntoView({ behavior: "smooth", block: "center" })
			}
		}
	}, [activity, date, loadCheck])

	useEffect(() => {
		if (activity && date) {
			checkActivityAvailablity({ activityId: activity.activityId, date, time })
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activity, date, time])

	if (!activity) {
		return null
	}

	if (!data && loadCheck) {
		return (
			<div className="mt-6 w-100">
				<div className="text-center f3 regular" ref={ref}>
					{massageFunnelSelectTime}
				</div>
				<Loader show={loadCheck} />
			</div>
		)
	}

	let times = []

	if (data && data.timeSpans && data.timeSpans.length) {
		let duration = activity.duration
		let current = new Date(data.timeSpans[0].start)
		let run = true

		do {
			let ts = data.timeSpans.find(e => new Date(e.start).toTimeString() == current.toTimeString())
			if (ts) {
				//If the time exists we can store it in our list and increase the current searchTime by the duration of the massage.
				times.push(ts)
				current.setMinutes(current.getMinutes() + duration)
			} else {
				//Get the next possible entry if no time exists we can break out of the loop
				let nextEntry = data.timeSpans.find(e => new Date(e.start).toTimeString() >= current.toTimeString())
				if (nextEntry) {
					current = new Date(nextEntry.start)
					continue
				} else {
					run = false
				}
			}
		} while (run)
	}

	times = times.filter(acc => acc.start.split("T")[1] > time.from && acc.end.split("T")[1] < time.to)

	return (
		<div className="mt-6 w-100">
			<div className="text-center f3 regular" ref={ref}>
				{massageFunnelSelectTime}
			</div>
			<Loader show={loadCheck} />
			{error && !loadCheck && <div className="f4 color-fg-danger text-center mt-3">{massageFunnelError}</div>}
			{data && !loadCheck && times.length === 0 && <div className="f4 color-fg-danger text-center mt-3">{massageFunnelNoTimeSlots}</div>}
			{data && !loadCheck && (
				<div className="mt-3 gridSelection">
					{times.map((acc, i) => {
						return (
							<div
								className={
									"primary border px-3 py-2 flex flex-center flex-column rounded-3 clickable" +
									classNamez({
										"color-bg-secondary": timeslot && timeslot.start === acc.start && timeslot.end === acc.end,
										"color-fg-on-secondary": timeslot && timeslot.start === acc.start && timeslot.end === acc.end,
									})
								}
								onClick={() => {
									setTimeslot(acc)
								}}
								key={i}
							>
								<div>{getTimeString(new Date(acc.start))}</div>
							</div>
						)
					})}
				</div>
			)}
		</div>
	)
}

function TicketVerification({ setDate, setZoneId, setTime, setLink }) {
	const params = new URLSearchParams(window.location.search)
	const internalParams = useSearchParams()[0]
	const ticketNumber = params.get("ticketNumber") || internalParams.get("ticketNumber")

	const {
		data: { massageFunnelNotice, massageFunnelError, massageFunnelErrorNoScanCode, errorInputMissingScanCode, buttonCheckTicket, labelScanCode },
	} = useGetStrings(undefined, { defaultData: {} })

	const [checkCode, { error, isLoading }] = useCheckRequiredVisitors()
	const {
		formState: { errors },
		register,
		handleSubmit,
		setValue,
	} = useForm()

	const raiseError = useRaiseError()

	const check = useCallback(() => {
		handleSubmit(async data => {
			try {
				const e = await checkCode({ ...data, ignoreUsed: true }).unwrap()
				if (e && (e.scanCodeTypeId === SCANCODE_TYPES.TICKET || e.oldRrooaarrTicket === 1)) {
					const zoneId = e.zoneId || (e.oldRrooaarrTicket === 1 ? 3 : undefined)
					if (appStore.get("core.location.availableMassageZones")?.indexOf(zoneId) === -1) {
						throw new Error("err.invalidZone")
					}
					setDate(new Date(e.dateFrom))
					setTime({
						from: e.timeFrom || "00:00:00",
						to: e.timeTo || "23:00:00",
					})

					setZoneId(zoneId)
					setLink(e.scanCode) //send scanCodeLink => also back in connector
				}
			} catch (e) {
				raiseError(e)
			}
		})()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [checkCode])

	useEffect(() => {
		if (ticketNumber) {
			setValue("scanCode", ticketNumber)
			check()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<>
			<div className="mb-6">{massageFunnelNotice}</div>
			<Input
				type="text"
				label={labelScanCode}
				formRegister={register("scanCode", {
					required: {
						value: true,
						message: errorInputMissingScanCode,
					},
				})}
				errors={errors}
			/>
			<div className="flex flex-center mt-6">
				<Button style="primary" onClick={check} isLoading={isLoading}>
					{buttonCheckTicket}
				</Button>
			</div>
			{error && !isLoading && (
				<div className="flex flex-center mt-6">
					<div className="f4 color-fg-danger text-center mt-3">
						{error instanceof ResponseError && error.response.data.errorMessage === "err.ScanCode.NotFound" ? (
							<>{massageFunnelErrorNoScanCode}</>
						) : (
							<>{massageFunnelError}</>
						)}
					</div>
				</div>
			)}
		</>
	)
}

export default function MassageBooking() {
	const { sm } = useViewport()
	const {
		data: { massageFunnelTitle, buttonCheckout, massageFunnelSubTitle, buttonAdd },
	} = useGetStrings(undefined, { defaultData: {} })
	const [createActivityReserveration] = useCreateActivityReservation()
	const raiseError = useRaiseError()
	const { push } = useCustomNavigation()
	const massages = useSelector("order.massages")
	const [state, dispatch] = useReducer(reducer, {
		date: undefined,
		group: undefined,
		type: undefined,
		time: undefined,
		activity: undefined,
		timeslot: undefined,
	})

	const onNext = useCallback(async () => {
		try {
			await createActivityReserveration({
				activityId: state.activity.activityId,
				startDateTime: state.timeslot.start,
				endDateTime: state.timeslot.end,
			})
				.unwrap()
				.then(e => {
					let uniqueId = e.tacBookingData.BookingIDs.UniqueID[0].value
					let date = new Date(e.startDateTime).toISOString().slice(0, 10)
					let activityId = e.activityId
					addMassage({
						exportCode: uniqueId,
						date,
						activityId,
						bookingLifeTimeEnd: e.displayBookingLifeTimeEnd,
						quantity: 1,
						zoneId: state.zoneId,
						timeFrom: e.startDateTime.split("T")[1],
						timeTo: e.endDateTime.split("T")[1],
						scanCodeLink: state.link,
						massage: state.activity.activity,
						grossAmount: state.activity.price,
					})

					dispatch({ type: "SET_ACTIVITY", payload: undefined })
					dispatch({ type: "SET_TIMESLOT", payload: undefined })
					dispatch({ type: "SET_TYPE", payload: undefined })
				})
		} catch (e) {
			raiseError(e, {}, false, true)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.activity, state.timeslot, state.zoneId, state.link])

	return (
		<OrderSubPage>
			<ContentHeader>
				<div>{massageFunnelTitle}</div>
				<div className="text-center color-fg-secondary mt-1 f3">{massageFunnelSubTitle}</div>
			</ContentHeader>
			<ContentBody className="flex-1 flex flex-column pt-2" scrollable>
				{!state.date && (
					<TicketVerification
						setDate={d => dispatch({ type: "SET_DATE", payload: d })}
						setZoneId={d => dispatch({ type: "SET_ZONE", payload: d })}
						setTime={d => dispatch({ type: "SET_TIME", payload: d })}
						setLink={d => dispatch({ type: "SET_LINK", payload: d })}
					/>
				)}

				{state.date && (
					<>
						<GroupSelection selectedGroup={state.group} setGroup={g => dispatch({ type: "SET_GROUP", payload: g })} />
						<TypeSelection group={state.group} type={state.type} setType={g => dispatch({ type: "SET_TYPE", payload: g })} />
						<ActivitySelection
							group={state.group}
							type={state.type}
							activity={state.activity}
							setActivity={g => dispatch({ type: "SET_ACTIVITY", payload: g })}
						/>
						<TimeSelection
							activity={state.activity}
							date={state.date}
							timeslot={state.timeslot}
							setTimeslot={g => dispatch({ type: "SET_TIMESLOT", payload: g })}
							time={state.time}
						/>
					</>
				)}
			</ContentBody>
			{sm && state.group && (
				<AnimatePresence>
					<m.div
						key="massage-selection-bottom"
						className="massageSelection of-hidden mt-4 color-bg-secondary-700 clickable boxShadow color-fg-white"
						initial={{
							height: 0,
						}}
						animate={{
							height: "auto",
							transition: {
								duration: 0.2,
								ease: "easeOut",
							},
						}}
						exit={{
							height: 0,
							transition: { duration: 0.2, ease: "easeOut" },
						}}
					>
						<div className="flex py-4 mx-3 flex-items-center">
							{state.group ? state.group.title : null} {state.activity ? state.activity.duration + " min" : null}
						</div>
					</m.div>
				</AnimatePresence>
			)}
			{state.date && (
				<ContentFooter
					buttonProps={{
						text: buttonAdd,
						disabled: !state.timeslot,
						onNext: onNext,
						isLoading: false,
					}}
				>
					{sm ? null : (
						<div
							className="flex-self-center semibold absolute"
							style={{
								left: "24px",
							}}
						>
							{state.group ? state.group.title : null} {state.activity ? state.activity.duration + " min" : null}
						</div>
					)}
					{massages?.length > 0 && (
						<Button
							style="primary"
							className="mr-3"
							onClick={() => {
								push("order/order-overview")
							}}
						>
							{buttonCheckout}
						</Button>
					)}
				</ContentFooter>
			)}
		</OrderSubPage>
	)
}

function reducer(state, { type, payload }) {
	if (type === "SET_DATE") {
		return { ...state, date: payload }
	} else if (type === "SET_GROUP") {
		return { ...state, group: payload, type: undefined, timeslot: undefined, activity: undefined }
	} else if (type === "SET_TYPE") {
		return { ...state, type: payload, activity: undefined, timeslot: undefined }
	} else if (type === "SET_ACTIVITY") {
		return { ...state, activity: payload, timeslot: undefined }
	} else if (type === "SET_TIMESLOT") {
		return { ...state, timeslot: payload }
	} else if (type === "SET_ZONE") {
		return { ...state, zoneId: payload }
	} else if (type === "SET_TIME") {
		return { ...state, time: payload }
	} else if (type === "SET_LINK") {
		return { ...state, link: payload }
	}

	return state
}
